import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { dataToDropDownConverter, hasPermission, LocalStorageHelper } from "../../../../helpers";
import { DropdownOptionModel, DropdownOptionValue } from "../../shared/dropdown/models";
import { BalanceOperationModal } from "../../shared/modal/balance-operation-modal";
import { getCurrentTheme } from "../../../../helpers/get-current-theme";
import { getObjectKeys } from "../../../../helpers/get-object-keys";
import { ThemeReverseMap } from "../../../../constants/theme";
import { LocalStorageKeys } from "../../../../constants";
import { Icons, UserType } from "../../../../enums";
import { CreatePopover } from "./create-popover";
import Permission from "../../shared/permission";
import CreateBusiness from "./create-business";
import { UserSettings } from "./user-settings";
import { Dropdown, Icon } from "../../shared";
import { useStore } from "../../../../hooks";
import UserWallet from "./user-wallet";

interface HeaderProps {}

const Header: React.FC<HeaderProps> = observer(() => {

  const { currenciesStore, userSettingsStore, partnerStore } = useStore();
  const { userCurrency, currencies: { data: { items: currencies } } } = currenciesStore;
  const { partnerBusiness: { data }, selectedBusiness } = partnerStore;
  const {
    userSettings: {
      data: {
        id,
        userType,
        limit: userLimit,
        balance: userBalance,
        walletCurrencyCode,
        partnerId,
      },
    },
  } = userSettingsStore;

  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [theme, setTheme] = useState(getCurrentTheme());
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  useEffect(() => {
    setSelectedCurrency(userCurrency.name as string);
  }, [userCurrency]);

  // TODO need do global for all using sections
  const currenciesDropdownData = useMemo<Array<DropdownOptionModel>>(() => {
    return dataToDropDownConverter(currencies, {
      valueKey: "name",
      labelKey: "name",
    });
  }, [currencies]);

  const handleCurrencyChange = useCallback((value?: DropdownOptionValue | Array<DropdownOptionValue>) => {
      userSettingsStore.editUserSettings({ currencyCode: value as string });
    }, []);

  const changeTheme = () => {
    const newTheme = ThemeReverseMap[theme];
    setTheme(newTheme);
    LocalStorageHelper.setItem(LocalStorageKeys.ViewMode, newTheme);
    document.documentElement.setAttribute("class", newTheme);
  };

  const balance = useMemo(() => {
    return userType === UserType.Partner
      ? selectedBusiness?.balance
      : userBalance;
  }, [userBalance, selectedBusiness]);

  const limit = useMemo(() => {
    return userType === UserType.Partner ? selectedBusiness?.limit : userLimit;
  }, [userLimit, selectedBusiness]);

  const currency = useMemo(() => {
    return userType === UserType.Partner
      ? selectedBusiness?.currencyCode
      : walletCurrencyCode;
  }, [userLimit, selectedBusiness]);

  const onClose = useCallback(() => {
    setIsOpenModal(  false);
  }, []);

  const openBalanceOperationModal = useCallback(() => {
    if (UserType.Manager === userType) {
      setIsOpenModal(true);
    }
  }, []);

  return (
    <div className="header">
      {/*TODO Multi currency*/}
      {hasPermission(userType as UserType, [UserType.Partner]) && data ? (
        <CreateBusiness
          businessData={data}
          currenciesDropdownData={currenciesDropdownData}
          partnerId={partnerId}
        />
      ) : null}
      <CreatePopover
        disableBTN={
          !getObjectKeys(selectedBusiness).length &&
          hasPermission(userType as UserType, [UserType.Partner])
        }
      />

      <Permission accessedUserTypes={[UserType.SuperAdmin, UserType.Admin]}>
        <Dropdown
          withSearch
          value={selectedCurrency}
          className="header-dropdown"
          data={currenciesDropdownData}
          onChange={handleCurrencyChange}
          isDrawSelectedItem={false}
        />
      </Permission>

      {
        <Permission accessedUserTypes={[UserType.Partner, UserType.Manager]}>
          <div className="balance-limit-info">
            <UserWallet
              isBalance
              currency={currency}
              userType={userType}
              amount={balance || 0}
              iconType={Icons.Balance}
              onClick={openBalanceOperationModal}
            />
            <UserWallet
              amount={limit || 0}
              currency={currency}
              userType={userType}
              iconType={Icons.Block}
            />
          </div>
        </Permission>
      }

      {/*TODO light dark mode*/}
      <div className="light-dark-mode-container" onClick={changeTheme}>
        <Icon
          type={theme === "dark" ? Icons.DarkMode : Icons.LightMode}
          wrap
          size="md"
        />
      </div>
      <UserSettings />
      {
         isOpenModal && id && (
          <BalanceOperationModal
            id={id}
            onClose={onClose}
            openWithHeader={true}
            currentBalance={balance || 0}
            currencyCode={currency || "USD"}
          />
        )
      }
    </div>
  );
});

export default memo(Header);
